import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FileText, Upload, CheckCircle, AlertTriangle, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const statusLabels: Record<string, { label: string; variant: "default" | "secondary" | "destructive" }> = {
  not_collected: { label: "Not Collected", variant: "destructive" },
  collected: { label: "W-9 Collected", variant: "default" },
  w9_uploaded: { label: "W-9 Collected", variant: "default" },
  uploaded: { label: "W-9 Collected", variant: "default" },
  verified: { label: "W-9 Collected", variant: "default" },
  filed: { label: "1099 Filed", variant: "secondary" },
};

const ContractorTaxInfo = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [taxInfo, setTaxInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [businessName, setBusinessName] = useState("");
  const [tinLastFour, setTinLastFour] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchTaxInfo();
  }, [user]);

  const fetchTaxInfo = async () => {
    if (!user) return;
    const [{ data }, { data: creds }] = await Promise.all([
      supabase
        .from("contractor_tax_info")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle(),
      supabase
        .from("provider_credentials")
        .select("w9_status")
        .eq("user_id", user.id)
        .maybeSingle(),
    ]);

    // If contractor_tax_info exists, use it
    if (data) {
      setTaxInfo(data);
      setBusinessName(data.business_name || "");
      setTinLastFour(data.tin_last_four || "");
    } else if (creds && ((creds as any).w9_status === "uploaded" || (creds as any).w9_status === "verified")) {
      // W-9 was uploaded during onboarding but no contractor_tax_info record yet
      setTaxInfo({
        filing_status: "collected",
        w9_collected_at: new Date().toISOString(),
        business_name: null,
        tin_last_four: null,
      });
    }
    setLoading(false);
  };

  const isPermissionOrTriggerError = (err: any): { friendly: boolean; reason?: string } => {
    const code = err?.code || "";
    const msg = (err?.message || "").toLowerCase();
    // Our enforce_contractor_tax_info_provider_only trigger raises 23514 (check_violation)
    if (code === "23514" || msg.includes("w-9 info can only be submitted")) {
      return {
        friendly: true,
        reason: "W-9 info can only be submitted once you've started a provider application. If you believe this is a mistake, contact support@elarahealth.life.",
      };
    }
    // Standard RLS rejection
    if (
      code === "42501" ||
      code === "PGRST301" ||
      msg.includes("row-level security") ||
      msg.includes("row level security") ||
      msg.includes("violates row-level") ||
      msg.includes("permission denied")
    ) {
      return {
        friendly: true,
        reason: "Your account doesn't have permission to submit W-9 info yet. Please contact support@elarahealth.life and we'll get you set up.",
      };
    }
    return { friendly: false };
  };

  const handleW9Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files?.[0]) return;
    setUploading(true);
    const file = e.target.files[0];
    const path = `${user.id}/w9-${Date.now()}.${file.name.split(".").pop()}`;

    const { error: uploadError } = await supabase.storage
      .from("w9-uploads")
      .upload(path, file);

    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from("w9-uploads").getPublicUrl(path);

    // Upsert tax info
    const upsertData = {
      user_id: user.id,
      w9_file_url: path,
      w9_collected_at: new Date().toISOString(),
      filing_status: "collected",
      business_name: businessName.trim() || null,
      tin_last_four: tinLastFour.trim() || null,
    };

    const { error } = taxInfo
      ? await supabase.from("contractor_tax_info").update(upsertData).eq("user_id", user.id)
      : await supabase.from("contractor_tax_info").insert(upsertData);

    if (error) {
      const { friendly, reason } = isPermissionOrTriggerError(error);
      toast({
        title: friendly ? "We couldn't save your W-9" : "Failed to save",
        description: friendly ? reason : error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "W-9 uploaded", description: "Your W-9 form has been saved." });
      fetchTaxInfo();
    }
    setUploading(false);
  };

  const saveInfo = async () => {
    if (!user) return;
    setSaving(true);

    const upsertData = {
      user_id: user.id,
      business_name: businessName.trim() || null,
      tin_last_four: tinLastFour.trim() || null,
    };

    const { error } = taxInfo
      ? await supabase.from("contractor_tax_info").update(upsertData).eq("user_id", user.id)
      : await supabase.from("contractor_tax_info").insert({ ...upsertData, filing_status: "not_collected" });

    if (error) {
      const { friendly, reason } = isPermissionOrTriggerError(error);
      toast({
        title: friendly ? "We couldn't save your W-9 info" : "Failed to save",
        description: friendly ? reason : error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "Info saved" });
      fetchTaxInfo();
    }
    setSaving(false);
  };

  if (loading) return null;

  const status = taxInfo?.filing_status || "not_collected";
  const statusConfig = statusLabels[status] || statusLabels.not_collected;
  const hasW9 = taxInfo?.w9_collected_at;

  // If W-9 already uploaded, show a compact confirmation view
  if (hasW9 && status !== "not_collected") {
    return (
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <FileText size={16} /> Tax Information (W-9 / 1099)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <CheckCircle size={20} className="text-primary shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">W-9 on file</p>
              <p className="text-xs text-muted-foreground">
                Submitted {new Date(taxInfo.w9_collected_at).toLocaleDateString()}
                {taxInfo.business_name && ` · ${taxInfo.business_name}`}
                {taxInfo.tin_last_four && ` · TIN ****${taxInfo.tin_last_four}`}
              </p>
            </div>
            <Badge variant={statusConfig.variant} className="ml-auto shrink-0">{statusConfig.label}</Badge>
          </div>

          <div className="flex items-center gap-3">
            {taxInfo.w9_file_url && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1.5"
                onClick={async () => {
                  const previewWindow = window.open("", "_blank");
                  if (!previewWindow) {
                    toast({ title: "Popup blocked", description: "Please allow popups for this site to view the W-9.", variant: "destructive" });
                    return;
                  }

                  previewWindow.document.title = "Loading W-9…";
                  previewWindow.document.body.innerHTML = "<div style='font-family: sans-serif; padding: 24px;'>Loading W-9…</div>";

                  const { data, error } = await supabase.storage
                    .from("w9-uploads")
                    .download(taxInfo.w9_file_url);

                  if (error || !data) {
                    previewWindow.close();
                    toast({ title: "Download failed", description: error?.message || "Could not load the W-9 file.", variant: "destructive" });
                    return;
                  }

                  const fileName = taxInfo.w9_file_url.toLowerCase();
                  const mimeType = fileName.endsWith(".pdf")
                    ? "application/pdf"
                    : fileName.endsWith(".png")
                      ? "image/png"
                      : fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")
                        ? "image/jpeg"
                        : data.type || "application/octet-stream";

                  const typedBlob = data.type === mimeType ? data : data.slice(0, data.size, mimeType);
                  const blobUrl = URL.createObjectURL(typedBlob);
                  previewWindow.location.replace(blobUrl);
                  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10 * 60_000);
                }}
              >
                <Download size={12} />
                View W-9
              </Button>
            )}
            <Label className="cursor-pointer">
              <div className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md hover:bg-muted transition-colors text-muted-foreground">
                <Upload size={12} />
                <span className="text-xs">{uploading ? "Uploading…" : "Replace W-9"}</span>
              </div>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleW9Upload}
                disabled={uploading}
              />
            </Label>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <FileText size={16} /> Tax Information (W-9 / 1099)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Status:</span>
          <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Business / Legal Name</Label>
            <Input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your legal name or business name"
            />
          </div>
          <div>
            <Label>TIN Last 4 Digits</Label>
            <Input
              value={tinLastFour}
              onChange={(e) => setTinLastFour(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="1234"
              maxLength={4}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div>
            <Label className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">
                <Upload size={14} />
                <span className="text-sm">{uploading ? "Uploading…" : "Upload W-9 Form"}</span>
              </div>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleW9Upload}
                disabled={uploading}
              />
            </Label>
          </div>
        </div>

        <Button onClick={saveInfo} disabled={saving} size="sm">
          {saving ? "Saving…" : "Save Tax Info"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ContractorTaxInfo;
